import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API, withCredentials: true });

api.interceptors.request.use((cfg) => {
  const t = localStorage.getItem("njm_token");
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

// Google-mode sessions use short-lived httpOnly cookies + a refresh cookie (demo-mode Bearer
// tokens are long-lived and have no refresh cookie, so this silently no-ops for them and falls
// through to the same logout-on-401 behavior as before).
const refreshInFlight = new Map();

// Shared by interceptor and cold-start bootstrap. Never mixes cookie families.
export function refreshCookieSession(endpoint) {
  if (!refreshInFlight.has(endpoint)) {
    const pending = api.post(endpoint, {}, { silent401: true }).finally(() => refreshInFlight.delete(endpoint));
    refreshInFlight.set(endpoint, pending);
  }
  return refreshInFlight.get(endpoint);
}

export async function loadSession(meUrl) {
  try { return await api.get(meUrl, { silent401: true, skipSessionRefresh: true }); }
  catch (error) {
    const endpoint = meUrl === "/auth/me" ? "/auth/refresh"
      : meUrl === "/v1/agency-auth/me" ? "/v1/agency-auth/refresh" : null;
    if (error?.response?.status !== 401 || !endpoint || localStorage.getItem("njm_token")) throw error;
    await refreshCookieSession(endpoint);
    return api.get(meUrl, { silent401: true, skipSessionRefresh: true });
  }
}

api.interceptors.response.use(
  (r) => r,
  async (e) => {
    const original = e.config || {};
    const url = original.url || "";
    const onAgency = window.location.pathname === "/agency" || window.location.pathname.startsWith("/agency/");
    const isAuthRoute = original.skipSessionRefresh || url.includes("/auth/login") || url.includes("/auth/refresh") || url.includes("/auth/me") || url.includes("/agency-auth/");
    if (e?.response?.status === 401 && !original._retried && !isAuthRoute) {
      original._retried = true;
      const hasBearerToken = !!localStorage.getItem("njm_token");
      try {
        // Demo/Bearer-mode accounts have NO refresh cookie at all (per the auth playbook,
        // /auth/refresh is a cookie-only concept for real Google sessions) — calling it here
        // always fails for them, and always did, turning any transient one-off blip (e.g. a
        // brief Mongo hiccup) into an immediate hard logout even though the SAME Bearer token
        // was still perfectly valid a moment later (reproduced independently by the testing
        // agent, ~20-30% of fresh page loads). For Bearer-mode, the correct "retry" is simply
        // re-sending the original request once — there is no separate token to refresh.
        if (!hasBearerToken) {
          // Build's Google/magic-link sessions use njm_access/njm_refresh, just like
          // /auth/refresh. Only the agency portal uses the separate agency cookies.
          await refreshCookieSession(onAgency ? "/v1/agency-auth/refresh" : "/auth/refresh");
        }
        return api(original);
      } catch (refreshErr) {
        // Failed refresh is evicted by refreshCookieSession.
      }
    }
    if (e?.response?.status === 401 && !original.silent401) {
      localStorage.removeItem("njm_token");
      localStorage.removeItem("njm_user");
      const dest = onAgency ? "/agency/login" : "/login";
      if (window.location.pathname !== dest) window.location.href = dest;
    }
    return Promise.reject(e);
  }
);

